import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  restrictionAction, restrictionMessage, subscribeToRestrictionNoticeCleared, subscribeToSubscriptionRestrictions, type SubscriptionRestriction,
} from '../../shared/subscriptionRestriction';
import './billing.css';

/**
 * EPN-105 — the global answer to a server `403` of `SUBSCRIPTION_ACCESS_RESTRICTED`, `SUBSCRIPTION_REQUIRED` or
 * `SUBSCRIPTION_UPGRADE_REQUIRED`, from ANY API call. `createJsonClient` announces the refusal once; `AppShell`
 * mounts this notice, which replaces what would otherwise be a raw "failed with 403" with plain language and the
 * one next step the server named (`requiredAction`). It is a presentation of the server's answer only — it neither
 * allows nor denies anything. It clears itself when the user navigates away.
 */
export function SubscriptionRestrictionNotice() {
  const { pathname } = useLocation();
  const [shown, setShown] = useState<{ restriction: SubscriptionRestriction; path: string } | null>(null);

  useEffect(
    () => subscribeToSubscriptionRestrictions((restriction) => setShown({ restriction, path: pathname })),
    [pathname],
  );
  // A screen that already explains this refusal beside its own form (the locked-feature dialog) asks for the global copy to go away.
  useEffect(() => subscribeToRestrictionNoticeCleared(() => setShown(null)), []);

  if (!shown || shown.path !== pathname) return null;
  const { restriction } = shown;
  const action = restrictionAction(restriction);

  return (
    <section
      className="access-restriction"
      role="alert"
      aria-label="Action blocked by subscription"
      data-testid="api-restriction-notice"
      data-code={restriction.code}
    >
      <h3 className="access-restriction__title"><span aria-hidden="true">🔒</span> This change is not available right now</h3>
      <p>{restrictionMessage(restriction)}</p>
      <div className="trial-card__actions">
        <Link className="access-restriction__cta" to={action.to}>{action.label}</Link>
        <button type="button" className="link" onClick={() => setShown(null)}>Dismiss</button>
      </div>
    </section>
  );
}
